/**
 * This is a comprehensive JavaScript module designed for managing subuser functionalities, including creation,
 * editing, listing, and validation within an application. It provides an interface to handle subuser data,
 * validate form inputs, perform CRUD operations, and interact with related API endpoints for subuser management.
 *
 * @module aimi_subuser
 * @param {Object} aimi_subuser - Main object for the subuser module, ensuring namespace integrity.
 * @param {Object} $ - The jQuery object used for DOM manipulation and AJAX requests.
 * @param {Object} app - Application-level object for core functionality integration.
 * @param {undefined} undefined - An undefined parameter used to safeguard against undefined global variable.
 */
(function (aimi_subuser, $, app, undefined) {
// Private object for internal module use
  let __this = {
    subusers: {}, // Stores subuser data
    $body: $('body'), // jQuery object for the body element

    /**
     * Validates the password fields within a specified form to ensure they match and meet defined criteria.
     * This method retrieves the values of the 'password' and 'confirm_password' fields, trims them for any leading
     * or trailing whitespace, and then compares them to ensure they are identical. If the passwords do not match,
     * the method adds a 'has-error' class to the 'confirm_password' field's closest '.form-group' container to visually
     * indicate the error to the user. This function is useful in forms where user password creation or updating is required,
     * ensuring user input is validated on the client side before submission.
     *
     * The function returns a boolean value - `true` if the password and confirmation match (indicating valid input),
     * and `false` otherwise.
     *
     * Usage example (assuming a form with name "user__edit"):
     * if (__this.validatePassword()) {
     *   // Proceed with form submission or further processing
     * } else {
     *   // Handle the validation error (e.g., display a message to the user)
     * }
     *
     * @returns {boolean} True if the password fields match and meet the criteria, false otherwise.
     */
    validatePassword: function () {
      let passwordInformationForm = $('form[name="user__edit"]');

      let fields = {
        'password': $('input[name="password"]', passwordInformationForm),
        'confirm_password': $('input[name="confirm_password"]', passwordInformationForm),
      };

      let values = {
        password: $.trim(fields.password.val()),
        confirm_password: $.trim(fields.confirm_password.val())
      };

      $.each(fields, function () {
        $(this).closest('.form-group').removeClass('has-error');
      });

      if (values.password !== values.confirm_password) {
        fields.confirm_password.closest('.form-group').addClass('has-error');
        return false;
      }

      return true;
    }
  }

  /**
   * Defines a set of API methods for managing subusers within the application. This includes functionalities
   * such as saving (or updating) subuser data, deleting a subuser, uploading subuser-related files (e.g., images),
   * and listing all subusers associated with a primary user account. These API methods make server-side requests
   * to perform the specified actions and handle the responses accordingly.
   *
   * Each method within this API utilizes specific request data and interacts with different endpoints on the server.
   * The responses from these requests are processed through callback functions, allowing for flexible handling of
   * success and error scenarios.
   *
   * Usage example:
   * app.apis['subuser'].save(requestData); // For saving or updating a subuser's data
   * app.apis['subuser'].delete(requestData); // For deleting a subuser
   * app.apis['subuser'].upload(inputElement); // For uploading a file associated with a subuser
   * app.apis['subuser'].list(); // For listing all subusers
   *
   * @namespace app.apis.subuser
   * @type {Object}
   */
  app.apis['subuser'] = function () {
    return {
      /**
       * Saves or updates subuser data based on the presence of an 'id' in the request data. If an 'id' is
       * not provided, a new subuser is created. If an 'id' is provided, the existing subuser with that 'id'
       * is updated. The method automatically adds certain properties to the request data, such as the plan
       * and parentId, before sending the request to the server.
       *
       * @param {Object} requestData - The data to be sent in the request. Should contain subuser-specific properties.
       * @returns {Promise} A promise that resolves with the server's response to the save or update request.
       */
      'save': function (requestData) {
        requestData.plan = 'proplus';
        requestData.parentId = app.apis['user'].get()['id'];
        requestData.config = aimi_subuser.view.getConfig();

        if (_.isEmpty(requestData.password)) {
          delete requestData.password;
        }

        if (_.isUndefined(requestData.id) || requestData.id === '') {
          return app.postRequest('/api/admin/subuser/', requestData, function (response) {
          }, function () {
          });
        } else {
          return app.putRequest(`/api/admin/subuser/${requestData.id}`, requestData, function (response) {
          }, function () {
          });
        }
      },
      /**
       * Sends a request to delete a subuser specified by the 'id' property in the requestData object. This method
       * makes a POST request to a specific endpoint designed for canceling (deleting) a subuser.
       *
       * @param {Object} requestData - The data for the request, expected to include the 'id' of the subuser to be deleted.
       * @returns {Promise} A promise that resolves with the server's response to the deletion request.
       */
      delete: function (requestData) {
        return app.postRequest(`/api/billing/cancel-subuser/${requestData.id}`, requestData, function (response) {
        });
      },
      /**
       * Handles the uploading of a file related to a subuser. This method constructs a FormData object to be
       * sent as part of the request, including the file selected by the user through an input element.
       *
       * @param {HTMLInputElement} input - The input element containing the file to be uploaded.
       * @returns {Promise} A promise that resolves with the server's response to the file upload request.
       */
      upload: function (input) {
        let data = new FormData();
        let file = input.files[0];
        data.append("file", file);
        return app.fileUploadRequest('/api/image/user', data, function (response) {
          __this.headshot = {
            id: response.id,
            path: response.path,
            pathThumbnail: response.pathThumbnail
          };
        }, function (response) {
          app.notify('error', response.message.message);
        })
      },
      /**
       * Retrieves a list of all subusers associated with the primary user account. This method makes a GET
       * request to a specific endpoint and processes the response accordingly.
       *
       * @returns {Promise} A promise that resolves with the server's response containing the list of subusers.
       */
      'list': function () {
        return app.getRequest('/api/user/subuser', {},
            function (response) {
            },
            function () {
            }
        );
      },
    }
  }();

  /**
   * Represents the view layer for managing subuser interactions within the AIMI subuser module. This includes
   * initializing the subuser form, editing subuser details, setting values within the form based on a subuser's
   * existing data, resetting the form to its default state, and gathering configuration data from the form.
   *
   * The view layer facilitates the addition of UI logic, such as form validation, file upload handling, and
   * dynamic form field updates. It relies on external libraries and components like Dropify for file uploads,
   * SimpleBar for custom scrollbars, and feather icons.
   *
   * Usage:
   * - `aimi_subuser.view.init()`: Prepares the subuser form and associated elements when the view is loaded.
   * - `aimi_subuser.view.edit(id)`: Populates the form with a subuser's existing data for editing.
   * - `aimi_subuser.view.setValues(values)`: Sets form fields based on a given subuser's data.
   * - `aimi_subuser.view.reset()`: Resets the form fields to their default states.
   * - `aimi_subuser.view.getConfig()`: Retrieves configuration data from the form, such as title and companyName.
   *
   * @namespace aimi_subuser.view
   */
  aimi_subuser.view = {

    /**
     * Initializes the subuser form and its components. This method sets up form validation,
     * Dropify for file inputs, event handlers for custom actions (e.g., file upload, image removal),
     * and populates timezone options. It also configures event listeners for form elements and
     * initializes custom scrollbars and icons.
     */
    init: function () {
      const $form = $('form[name=aimi-sub-user]');
      const $headshotActions = $('.headshot-actions');
      const $headShotInput = $(':input[name=headshot]');

      __this.$removeHeadshotButton = $('.btn-remove-Headshot');
      __this.$removeHeadshotButton.hide();

      let $password = $(':input[name=password]', $form)[0];
      let $confirmPassword = $(':input[name=confirm_password]', $form)[0];

      __this.validatePassword = function () {
        if ($password.value != $confirmPassword.value) {
          $confirmPassword.setCustomValidity("Passwords Don't Match");
        } else {
          $confirmPassword.setCustomValidity('');
        }
      };

      $password.onchange = __this.validatePassword;
      $confirmPassword.onkeyup = __this.validatePassword;

      let drEvent = $headShotInput.dropify({
        messages: {
          'default': '<span class="main-text">Take or Choose Image</span><span class="sub-text"> The app will only accept headshots in jpg, jpeg, and png file formats with a max file size of 10mb.</span>',
          'replace': 'Drag and drop or click to replace',
          'remove': 'Remove',
          'error': 'Ooops, something wrong happended.'
        },
        tpl: {
          message: '<div class="dropify-message"><span class="file-icon"></span> <p>{{ default }}</p></div>',
        }
      });

      drEvent.on('dropify.afterClear', function (event, element) {
        $headshotActions.addClass('d-none');
      });

      drEvent.on('dropify.fileReady', function (event, element) {
        $headshotActions.addClass('d-none');
        const $uploadImage = $('.headshot-actions.btn-upload-image');
        $uploadImage.removeClass('d-none');
      });

      const page = _.template(app.templates['global__timzone']);
      const $page = $(page({}));

      // Set timezone select options
      $('select[name="timezone"]').append($page);

      $(document).off('click.aimi_subuser_view_upload').on('click.aimi_subuser_view_upload', '.btn-upload-image', function (e) {
        const $button = $(this);
        app.doButtonAction($button);
        $.when(app.apis['subuser'].upload($headShotInput[0])).done(function () {
          const $form = $('form[name=aimi-sub-user]');
          let requestData = $form.serializeObject();
          app.apis['subuser'].save(requestData).done(function (response) {
            app.doButtonReset($button);
            aimi_subuser.updateCallBack(response);
          }).fail(function (response) {
            app.doButtonReset($button);
          });
        }).fail(function (response) {
          app.doButtonReset($button, false);
        })
      });

      $(document).off('click.aimi_subuser_view_remove').on('click.aimi_subuser_view_remove', '.btn-remove-image', function (e) {
        const $form = $('form[name=aimi-sub-user]');
        const $button = $(this);
        __this.headshot = {};
        app.doButtonAction($button);
        let requestData = $form.serializeObject();
        app.apis['subuser'].save(requestData).done(function (reponse) {
          app.doButtonReset($button);
          aimi_subuser.updateCallBack(reponse);
        }).fail(function (response) {
          app.doButtonReset($button, false);
        });
      });

      new SimpleBar(document.getElementById('tab_block_1'), {autoHide: true});
      new SimpleBar(document.getElementById('tab_block_2'), {autoHide: true});

    },

    /**
     * Prepares the form for editing an existing subuser's details. This involves selecting the
     * appropriate tab and form section, and populating the form fields with the subuser's data.
     *
     * @param {number} id - The unique identifier of the subuser to be edited.
     */
    edit: function (id) {
      let __list = aimi_subuser.list;
      const triggerEl = document.querySelector('.pg-header-wth-tab a[href="#tab_block_2"]')
      const tab = new bootstrap.Tab(triggerEl)
      tab.show() // Select tab by name
      __list.setViewTabTitle(`Edit Sub User #${id}`);
      this.setValues(__this.subusers[id]);
    },

    /**
     * Sets the form values based on the provided subuser data. This method is typically called when
     * editing an existing subuser, filling in the form fields with the subuser's current information.
     *
     * @param {Object} values - An object containing subuser data to populate the form with.
     */
    setValues: function (values) {
      app.clearDropify();
      const $form = $('form[name=aimi-sub-user]');
      $(':input[name=id]', $form).val(values.id);
      $(':input[name=email]', $form).val(values.email);
      $(':input[name=firstname]', $form).val(values.firstname);
      $(':input[name=lastname]', $form).val(values.lastname);
      $(':input[name=timezone]', $form).val(values.timezone);
      $(':input[name=phone]', $form).val(values.phone);
      const $removeImage = $('.headshot-actions.btn-remove-image');

      let timezone = $(':input[name=timezone]', $form).mobiscroll('getInst');

      if (typeof timezone.setVal !== 'undefined') {
        timezone.setVal(values.timezone);
        timezone.refresh();
      }

      let userConfig = JSON.parse(values.affiliateData);

      if (_.isUndefined(userConfig) || _.isNull(userConfig)) {
        userConfig = {};
      }

      if (!_.isUndefined(userConfig.headshot) && !_.isEmpty(userConfig.headshot)) {
        let $render = $('.dropify-preview');
        let $img = $('<img />').attr('src', userConfig.headshot.path);
        $removeImage.removeClass('d-none')
        $img.appendTo($('.dropify-render', $render));
        $render.show();

        __this.headshot = {
          id: userConfig.headshot.id,
          path: userConfig.headshot.path,
          pathThumbnail: userConfig.headshot.pathThumbnail
        };
      }

      if (!_.isUndefined(userConfig.companyName) && !_.isEmpty(userConfig.companyName)) {
        $(':input[name=companyName]', $form).val(userConfig.companyName);
      }

      if (!_.isUndefined(userConfig.title) && !_.isEmpty(userConfig.title)) {
        $(':input[name=title]', $form).val(userConfig.title);
      }

      $(':input[name=password]', $form).removeAttr('required');
      $(':input[name=confirm_password]', $form).removeAttr('required');
    },
    /**
     * Resets the form to its default state. This is typically used when transitioning from editing
     * a subuser to creating a new one, ensuring that the form fields are cleared and reset.
     */
    reset: function () {
      app.clearDropify();
      const $form = $('form[name=aimi-sub-user]');
      $(':input[name=id]', $form).val('');
      $(':input[name=email]', $form).val('');
      $(':input[name=firstname]', $form).val('');
      $(':input[name=lastname]', $form).val('');
      $(':input[name=timezone]', $form).val('');
      $(':input[name=phone]', $form).val('');
      $(':input[name=companyName]', $form).val('');
      $(':input[name=title]', $form).val('');
      $(':input[name=headshot]', $form).val('');
      $(':input[name=password]', $form).attr('required', 'required');
      $(':input[name=confirm_password]', $form).attr('required', 'required');
      $form.removeClass('was-validated');
    },

    /**
     * Retrieves configuration data from the form, such as the subuser's title, companyName, and headshot.
     * This data is gathered into an object that can be used for saving or updating a subuser's details.
     *
     * @returns {Object} An object containing configuration data from the form.
     */
    getConfig: function () {
      const $form = $('form[name=aimi-sub-user]');
      return {
        'title': $(':input[name=title]', $form).val(),
        'companyName': $(':input[name=companyName]', $form).val(),
        'headshot': __this.headshot,
      }
    }
  }

  /**
   * Manages the display and interaction logic for a list of subusers within the application.
   * This includes preparing data rows for display, showing a loading animation during data retrieval,
   * rendering the list of subusers, initializing event listeners for list interactions,
   * and handling the dynamic update of tab titles based on user actions.
   */
  aimi_subuser.list = {
    /**
     * Prepares a data row for a single subuser for display. This includes templating the row
     * and formatting date fields.
     * @param {Object} row - The raw subuser data object to be prepared for display.
     * @returns {jQuery} - The jQuery object representing the templated and formatted row ready for insertion into the DOM.
     */
    prepareRow: function (row) {
      const page = _.template(app.templates['subuser__row']);
      const $page = $(page({
        ...row,
        'dateCreated_ll': moment(moment.utc(row.dateCreated.date).toDate()).format('ll'),
        'dateCreated_LT': moment(moment.utc(row.dateCreated.date).toDate()).format('LT'),
        'cancelationDate': _.isEmpty(row.cancelationDate) ? '' : moment(moment.utc(row.cancelationDate.date).toDate()),
      }));
      return $page;
    },
    /**
     * Shows a placeholder loading animation in the specified list container. This gives visual feedback
     * to the user that data is being loaded.
     * @param {jQuery} $listContainer - The jQuery object representing the container where the loading placeholders are to be shown.
     */
    showLoader: function ($listContainer) {
      const staticPH = '<p class=" placeholder-glow"><span class="placeholder col-8"></span></p>';
      let row = {
        id: null,
        status: staticPH,
        dateCreated_ll: staticPH,
        dateCreated_LT: staticPH,
        email: staticPH,
        firstname: staticPH,
        lastname: '',
        cancelationDate: staticPH
      }
      const page = _.template(app.templates['subuser__row']);
      let $row = $(page(row));
      $row.addClass('pre-loader');
      for (let i = 0; i < 8; i++) {
        $row.clone().appendTo($listContainer);
      }
    },
    /**
     * Renders the list of subusers by fetching the subuser data and then iterating over the response
     * to prepare and display each row.
     */
    renderList: function () {
      const $listContainer = $('[data-subusers]');
      let query = $.Deferred();
      let __list = this;
      $listContainer.empty();
      __list.showLoader($listContainer);
      $.when(app.apis['subuser'].list()).done(function (response) {
        $listContainer.empty();
        if (typeof response.result != 'undefined' && response.result.length) {
          _.each(response.result, function (row) {
            __this.subusers[row.id] = row;
            let $row = __list.prepareRow(row);
            $row.appendTo($listContainer);
          });
        }
        return query.resolve(response);
      });
      return query.promise();
    },
    /**
     * Initializes the list component by setting up necessary event listeners and fetching/rendering
     * the list of subusers.
     */
    init: function () {
      let __list = this;
      let __view = aimi_subuser.view;

      __list.getOriginalTabs();
      __list.renderList();

      $(document).off('click.aimi_subvalues_list').on('click.aimi_subvalues_list', '.subuser-row .btn-edit', function (e) {
        aimi_subuser.view.edit($(this).data('id'));
      });

      $(document).off("click.bs.tab").on("click.bs.tab", ".hk-pg-header a[data-bs-toggle='tab']", function (e) {
        let $this = $(this);
        const href = $this.attr('href');
        __list.setViewTabTitle();
        __view.reset();
      });
    },
    /**
     * Retrieves and stores the original titles of tabs within the page, allowing for dynamic updates
     * to tab titles based on user interaction.
     */
    getOriginalTabs: function () {
      __this.tabs = {};
      _.each($('.hk-pg-header a[data-bs-toggle="tab"]'), function (row) {
        let $row = $(row);
        __this.tabs[$row.attr('href')] = $('.nav-link-text', $row).text();
      });
    },
    /**
     * Dynamically sets the title of a view or edit subuser tab. If no title is specified, resets
     * the title to its original value.
     * @param {string|null} title - The new title for the tab or null to reset to the original title.
     */
    setViewTabTitle: function (title = null) {
      const subuserTab = '#tab_block_2';
      let $newUserTab = $(`a[href="${subuserTab}"]`);
      if (title == null) {
        title = __this.tabs[subuserTab];
      }
      $('.nav-link-text', $newUserTab).text(title)
    }
  }

  /**
   * Callback function to be called after a subuser update operation. It notifies the user of the
   * operation's success, updates the subuser list, and resets the view with the updated subuser's values.
   * @param {Object} response - The response object from the subuser update operation, containing at least the ID of the updated subuser.
   */
  aimi_subuser.updateCallBack = function (response) {
    response.message = 'Subuser saved successfully';
    app.notifyEl.update({'message': response.message, progress: 90});
    aimi_subuser.list.renderList().then(function () {
      aimi_subuser.view.setValues(__this.subusers[response.id]);
    });
  }
}(window.aimi_subuser = window.aimi_subuser || {}, jQuery, PreBuilt));
